import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import { FaArrowRight, FaCheckCircle, FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import { IoChevronBack } from "react-icons/io5";
import FamilyHeader from "./FamilyHeader";
import PasswordCriteriaList from "../../common/components/PasswordCriteriaList";
import { RESET_PASSWORD_SUCCESS_MESSAGE, resetAuthenticatedPassword } from "../../common/api/resetPasswordApi";
import { PROFILE_ROLE_FAMILY_MEMBER } from "../../common/api/profileApi";
import { getStoredUserEmail } from "../../common/utils/sessionContext";
import { isPasswordValid } from "../../common/utils/passwordCriteria";

const FamilyResetPassword = () => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentPasswordError, setCurrentPasswordError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const passwordMeetsCriteria = isPasswordValid(password);
  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const canSubmit = currentPassword.length > 0 && passwordMeetsCriteria && passwordsMatch && !isSubmitting && !isSuccess;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setCurrentPasswordError("");
    setPasswordError("");

    if (!canSubmit) {
      return;
    }

    const emailId = getStoredUserEmail();
    if (!emailId) {
      setPasswordError("Unable to reset password. Please sign in again.");
      return;
    }

    setIsSubmitting(true);
    const result = await resetAuthenticatedPassword({
      emailId,
      currentPassword,
      password,
      confirmPassword,
      role: PROFILE_ROLE_FAMILY_MEMBER,
    });
    setIsSubmitting(false);

    if (!result.success) {
      if (result.field === "currentPassword") {
        setCurrentPasswordError(result.message);
      } else {
        setPasswordError(result.message);
      }
      return;
    }

    setIsSuccess(true);
  };

  const goToProfile = () => {
    navigate("/family-settings", { replace: true });
  };

  return (
    <div className="family-page-layout family-settings-layout">
      <FamilyHeader />
      <Backdrop sx={{ color: "#ffba30", zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={isSubmitting}>
        <img src={require("../../assets/btTree.png")} className="loading-img" alt="" />
      </Backdrop>

      <div className="family-settings-page family-profile-page family-reset-password-page">
        <div className="family-checklist-topbar">
          <button type="button" className="family-checklist-back" onClick={goToProfile} aria-label="Back to profile" disabled={isSuccess}>
            <IoChevronBack />
          </button>
          <h1 className="family-checklist-title-center">Reset password</h1>
          <span className="family-vitals-topbar-spacer" aria-hidden="true" />
        </div>

        {isSuccess ? (
          <div className="family-reset-password-success-wrap">
            <div className="bt-forgot-success-card family-reset-password-success-card" role="status" aria-live="polite">
              <FaCheckCircle className="bt-forgot-success-card-icon" aria-hidden="true" />
              <h2 className="bt-forgot-success-card-title">{RESET_PASSWORD_SUCCESS_MESSAGE}</h2>
              <button type="button" className="bt-login-submit bt-forgot-success-card-btn" onClick={goToProfile}>
                <IoChevronBack aria-hidden="true" />
                <span>back to profile</span>
              </button>
            </div>
          </div>
        ) : (
          <form className="family-reset-password-form" onSubmit={handleSubmit}>
            <p className="family-reset-password-intro">Enter your current password and choose a new password for your account.</p>

            <div className="family-profile-field">
              <label htmlFor="family-reset-current-password" className="family-profile-field-label">
                Current password
              </label>
              <div className="family-reset-password-input-wrap">
                <FaLock className="family-reset-password-input-icon" aria-hidden="true" />
                <input
                  id="family-reset-current-password"
                  type={showCurrentPassword ? "text" : "password"}
                  className="family-profile-field-input family-reset-password-input"
                  value={currentPassword}
                  onChange={(e) => {
                    setCurrentPassword(e.target.value);
                    setCurrentPasswordError("");
                    setPasswordError("");
                  }}
                  autoComplete="current-password"
                  required
                />
                <button type="button" className="family-reset-password-toggle" onClick={() => setShowCurrentPassword((open) => !open)} aria-label={showCurrentPassword ? "Hide current password" : "Show current password"}>
                  {showCurrentPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>
              {currentPasswordError ? (
                <p className="family-reset-password-error family-reset-password-error--field" role="alert">
                  {currentPasswordError}
                </p>
              ) : null}
            </div>

            <div className="family-profile-field">
              <label htmlFor="family-reset-new-password" className="family-profile-field-label">
                New password
              </label>
              <div className="family-reset-password-input-wrap">
                <FaLock className="family-reset-password-input-icon" aria-hidden="true" />
                <input
                  id="family-reset-new-password"
                  type={showPassword ? "text" : "password"}
                  className="family-profile-field-input family-reset-password-input"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError("");
                  }}
                  autoComplete="new-password"
                  required
                />
                <button type="button" className="family-reset-password-toggle" onClick={() => setShowPassword((open) => !open)} aria-label={showPassword ? "Hide new password" : "Show new password"}>
                  {showPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>
              <PasswordCriteriaList password={password} />
            </div>

            <div className="family-profile-field">
              <label htmlFor="family-reset-confirm-password" className="family-profile-field-label">
                Confirm new password
              </label>
              <div className="family-reset-password-input-wrap">
                <FaLock className="family-reset-password-input-icon" aria-hidden="true" />
                <input
                  id="family-reset-confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  className="family-profile-field-input family-reset-password-input"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setPasswordError("");
                  }}
                  autoComplete="new-password"
                  required
                />
                <button type="button" className="family-reset-password-toggle" onClick={() => setShowConfirmPassword((open) => !open)} aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}>
                  {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>
            </div>

            {passwordError ? (
              <p className="family-reset-password-error" role="alert">
                {passwordError}
              </p>
            ) : null}

            <button type="submit" className="bt-login-submit family-reset-password-submit" disabled={!canSubmit}>
              <span>{isSubmitting ? "Resetting…" : "Reset password"}</span>
              <FaArrowRight aria-hidden="true" />
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default FamilyResetPassword;
